import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton, Skeleton } from '@mui/material';
import Grid from '@mui/material/Grid';
import QrCodeIcon from '@mui/icons-material/QrCode';

import { useAsyncData } from 'src/hooks/useAsyncData';
import { isLoading, isSuccess } from 'src/utils/request';
import { MerchantTitle } from 'app/MerchantTitle';
import { awardsApi } from 'components/awards/awardsApi';
import { IAwardsResponse } from 'components/awards/models';
import { AwardsList } from 'components/awards/ui/List';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { ELoyaltyProvider } from 'components/awards/enums';
import { AwardUserSection } from 'components/awards/ui/user-section/AwardUserSection';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const PREFIX = 'AwardsPage';
const classes = {
    loadingSkeleton: `${PREFIX}loadingSkeleton`
};
const Root = styled(Box)(({ theme }) => ({
    [`& .${classes.loadingSkeleton}`]: {
        borderRadius: theme.shape.borderRadius
    }
}));

export const AwardsPage: React.FC = () => {
    const { t } = useTranslation();
    const [awardsResponse, setAwardsResponse] = useAsyncData<IAwardsResponse>();
    const [showBarcodeDialog, setShowBarcodeDialog] = React.useState(false);
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    React.useEffect(() => {
        if (settings) {
            setAwardsResponse(() =>
                awardsApi.getAwards(
                    settings?.loyaltyProvider === ELoyaltyProvider.Square ||
                        settings?.loyaltyProvider === ELoyaltyProvider.Powercard
                )
            );
        }
    }, [setAwardsResponse, settings]);

    const loadingSkeleton = React.useMemo(() => {
        const skeletonArray = [];
        for (let i = 0; i < 8; i++) {
            skeletonArray.push(
                <Grid
                    key={i}
                    size={{
                        xs: 12,
                        sm: 4,
                        md: 3
                    }}
                >
                    <Skeleton
                        className={classes.loadingSkeleton}
                        variant="rectangular"
                        animation="wave"
                        height={200}
                    />
                </Grid>
            );
        }

        return (
            <Grid container spacing={1}>
                {skeletonArray}
            </Grid>
        );
    }, []);

    const { logUserEvent } = useGAHelpers();

    const barcodeIconEnabled =
        settings?.offlineCheckinEnabled && settings?.app?.awardsScreenBarcodePosition === 'TOOLBAR_BUTTON';
    const handleBarcodeIconClick = React.useCallback(() => {
        logUserEvent(EGAEventName.ShowBarcodeIconTap);
        setShowBarcodeDialog(true);
    }, [logUserEvent]);
    const handleBarcodeDialogClose = React.useCallback(() => {
        setShowBarcodeDialog(false);
    }, []);

    return (
        <Root>
            <MerchantTitle title={t('AWARDS_TITLE')} />
            <MuiAppBar title={t('AWARDS_TITLE')}>
                {barcodeIconEnabled && (
                    <IconButton onClick={handleBarcodeIconClick} size="large">
                        <QrCodeIcon color="primary" />
                    </IconButton>
                )}
            </MuiAppBar>
            {isSuccess(awardsResponse) && awardsResponse.data && (
                <AwardsList awards={awardsResponse.data.awards} />
            )}
            {isLoading(awardsResponse) && <Box padding={2}>{loadingSkeleton}</Box>}
            {barcodeIconEnabled && (
                <Dialog fullWidth open={showBarcodeDialog} onClose={handleBarcodeDialogClose}>
                    <Box padding={2}>
                        <AwardUserSection />
                    </Box>
                </Dialog>
            )}
        </Root>
    );
};
